import { ProductType } from 'components/shared/Cart/types';
import {
  isLabPriceCutWindow,
  LAB_PRICE_CUT_DISCOUNT_CODE,
} from 'components/performance-lab/buy/india/priceCut';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { CartPolicy } from './types';
import { getAttr, pass } from './cartLineHelpers';

const CODE = LAB_PRICE_CUT_DISCOUNT_CODE;

const lineIsPerformanceLab = (attributes?: { key: string; value: string }[]) =>
  getAttr(attributes, '_product_type') === ProductType.PERFORMANCE_LAB;

/**
 * Auto-attach EXINPL15 when a Performance Lab line is added during the festive
 * window on the India store. Not auto-added over another queued discount
 * (affiliate / referral / rep / UHX); never removed once the caller queued it.
 *
 * Add-only: qty updates omit `discountCodes`, and emitting one here would
 * replace the Shopify cart's codes (wiping an affiliate).
 */
export const plExinpl15Policy: CartPolicy = (input, cart, meta) => {
  if (meta.store !== ShopifyStore.IN) return pass(input);
  if (!isLabPriceCutWindow()) return pass(input);

  const addingPl = (input.lineItemsToAdd ?? []).some((line) =>
    lineIsPerformanceLab(line.attributes),
  );
  const cartHasPl = Boolean(
    cart?.lines?.edges?.some((edge) =>
      lineIsPerformanceLab(edge.node.attributes),
    ),
  );
  if (!addingPl && !cartHasPl) return pass(input);

  const codes = [...(input.discountCodes ?? [])];

  // A caller that queued EXINPL15 itself keeps it, alongside any other code
  // (e.g. a `?discount=` code appended later). The codes don't combine in
  // Shopify: it applies the single best one, so the card price is a ceiling.
  if (codes.includes(CODE)) {
    return pass(input, 'EXINPL15 already queued');
  }
  if (codes.some(Boolean)) {
    return pass(input, 'competing code present; skip EXINPL15');
  }

  return {
    input: {
      ...input,
      discountCodes: [...codes, CODE],
    },
    reasons: ['auto-add EXINPL15 for Performance Lab line'],
  };
};
